import { useMemo, useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useMembers } from '@/hooks/api/useMembers';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { canJoinSecurityGroup } from '@/lib/member-kinds';
import { getUserDisplayName } from '@/lib/utils';
import { UserTypeTag } from '@/components/grouped-alerts/UserTypeTag';
import {
  useAddSecurityGroupMember,
  useSecurityGroupByUser,
} from '@/hooks/api/useSecurityGroups';

/**
 * "Add users" picker for a security group (KIR-3800, AC #3).
 *
 * A user belongs to at most one group, and this dialog does **not** move people
 * between groups: anyone already in a different group is filtered out of the
 * list entirely, with a footnote saying how many were hidden and why. Regrouping
 * a user is therefore an explicit two-step act — remove them from their current
 * group first — rather than a side effect of ticking a box, because it silently
 * changes which policies apply to them.
 */
export function AddGroupMembersDialog({
  isOpen,
  onClose,
  organizationId,
  groupId,
  existingUserIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  groupId: string;
  /** Descope IDs already in this group — filtered out of the picker. */
  existingUserIds: string[];
}): ReactElement {
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      {/*
        Mounted only while open so the search box and selection start empty on
        every open, without an effect resetting state after the fact.
      */}
      {isOpen ? (
        <AddGroupMembersForm
          organizationId={organizationId}
          groupId={groupId}
          existingUserIds={existingUserIds}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
}

function AddGroupMembersForm({
  organizationId,
  groupId,
  existingUserIds,
  onClose,
}: {
  organizationId: string;
  groupId: string;
  existingUserIds: string[];
  onClose: () => void;
}): ReactElement {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: members, isLoading } = useMembers(organizationId);
  const { groupByUserId } = useSecurityGroupByUser(organizationId);
  const addMemberMutation = useAddSecurityGroupMember(organizationId);
  // KIR-5410: with shared devices, people without a Descope account can join.
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.SHARED_DEVICES]);
  const sharedDevicesEnabled =
    flags[flagsmithFeatureFlags.SHARED_DEVICES] ?? false;

  const candidates = useMemo(() => {
    const alreadyInGroup = new Set(existingUserIds);
    const term = search.trim().toLowerCase();
    return (
      (members ?? [])
        // KIR-3908: Discovered/Unattributed rows are not real accounts (their
        // `id` is the internal users.id, not a Descope id). KIR-5410: with
        // shared devices on, the backend accepts them by that internal id, so
        // they are offered with their label; offboarded people never are.
        .filter(member => canJoinSecurityGroup(member, sharedDevicesEnabled))
        .filter(member => !alreadyInGroup.has(member.id))
        // A user belongs to at most one group, and this dialog does not move
        // people between groups: someone already in another group is simply not
        // offered. To regroup them, remove them from their current group first.
        // That keeps the destructive act (silently changing which policies apply
        // to a user) an explicit, separate decision.
        .filter(member => !groupByUserId[member.id])
        .filter(member => {
          if (!term) return true;
          return (
            (member.name ?? '').toLowerCase().includes(term) ||
            (member.email ?? '').toLowerCase().includes(term)
          );
        })
    );
  }, [members, existingUserIds, groupByUserId, search, sharedDevicesEnabled]);

  // Surfaced as a hint so the list not showing a known colleague is explicable
  // rather than looking like a bug.
  const inOtherGroupCount = useMemo(() => {
    const alreadyInGroup = new Set(existingUserIds);
    return (members ?? []).filter(
      member =>
        canJoinSecurityGroup(member, sharedDevicesEnabled) &&
        !alreadyInGroup.has(member.id) &&
        groupByUserId[member.id]
    ).length;
  }, [members, existingUserIds, groupByUserId, sharedDevicesEnabled]);

  const toggle = (userId: string): void => {
    setSelected(current =>
      current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId]
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (selected.length === 0) return;
    try {
      // Sequential rather than concurrent: each add can move the user out of
      // another group, and the backend enforces the one-group-per-user
      // invariant per request. Serial keeps the resulting membership
      // deterministic and keeps error attribution simple.
      for (const userId of selected) {
        await addMemberMutation.mutateAsync({ groupId, userId });
      }
      toast({
        title: 'Success',
        description:
          selected.length === 1
            ? '1 user added to the group'
            : `${selected.length} users added to the group`,
        variant: 'success',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add users');
    }
  };

  return (
    <DialogContent
      // Adding is a multi-request loop; keep the dialog open until it settles so
      // a partial failure is reported rather than silently dismissed.
      onEscapeKeyDown={event =>
        addMemberMutation.isPending && event.preventDefault()
      }
      onInteractOutside={event =>
        addMemberMutation.isPending && event.preventDefault()
      }
    >
      <DialogHeader>
        <DialogTitle>Add users to this group</DialogTitle>
        <DialogDescription>
          Users take this group&apos;s policies. Only users who are not in
          another security group can be added.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <Input
          value={search}
          placeholder="Search by name or email"
          aria-label="Search users"
          onChange={event => setSearch(event.target.value)}
        />

        <div className="max-h-64 space-y-1 overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading users...</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {search.trim()
                ? 'No available users match your search.'
                : inOtherGroupCount > 0
                  ? 'No available users. Everyone else is already in this or another security group.'
                  : 'Every user in this organization is already in this group.'}
            </p>
          ) : (
            candidates.map(member => (
              // The Checkbox primitive renders its own <label>, so this row is a
              // div (nested labels are invalid) with an explicit aria-label
              // carrying the member identity for the a11y tree.
              <div
                key={member.id}
                className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted"
              >
                <Checkbox
                  checked={selected.includes(member.id)}
                  onChange={() => toggle(member.id)}
                  aria-label={`Select ${member.email ?? getUserDisplayName(member)}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5 text-sm">
                    <span className="truncate">
                      {getUserDisplayName(member)}
                    </span>
                    <UserTypeTag
                      userType={member.userType}
                      named={!!member.name}
                    />
                  </span>
                  {member.name && member.email ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {member.email}
                    </span>
                  ) : null}
                </span>
              </div>
            ))
          )}
        </div>

        {inOtherGroupCount > 0 ? (
          <p className="text-xs text-muted-foreground">
            {inOtherGroupCount === 1
              ? '1 user is hidden because they already belong to another security group.'
              : `${inOtherGroupCount} users are hidden because they already belong to another security group.`}
          </p>
        ) : null}

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={addMemberMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={selected.length === 0 || addMemberMutation.isPending}
        >
          {addMemberMutation.isPending
            ? 'Adding...'
            : `Add ${selected.length || ''}`.trim()}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
